import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Send, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import AdSlot from "../components/AdSlot";
import { API } from "../lib/api";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEEB]">
      <SiteHeader />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <ShweebumCat size="lg" />
            <h1 className="font-literary text-3xl sm:text-4xl text-foreground mt-4" data-testid="contact-title">
              Contact Us
            </h1>
            <p className="text-muted-foreground mt-2">We'd love to hear from you!</p>
            <p className="text-muted-foreground text-sm mt-1">Or email us directly at <a href="mailto:shweebum@outlook.com" className="text-cyan-300 hover:underline">shweebum@outlook.com</a></p>
          </motion.div>

          {submitted ? (
            <Card className="bg-card">
              <CardContent className="p-8 text-center">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="font-literary text-2xl text-foreground mb-2">Message Sent!</h2>
                <p className="text-muted-foreground mb-4">
                  Thank you for reaching out. We'll get back to you as soon as possible.
                </p>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  Send Another Message
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="What's this about?"
                      data-testid="contact-subject"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us what's on your mind..."
                      className="min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-cyan-700 hover:bg-cyan-800"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? "Sending..." : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Info */}
          <AdSlot slot="contact-bottom" format="inline" className="max-w-2xl mx-auto" />
          <div className="mt-8 text-center text-muted-foreground">
            <p className="flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4" />
              Southeast United States
            </p>
            <p className="mt-2 text-sm">
              You Inspire Designs LLC — A Family Business
            </p>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;
